import { useEffect, useRef } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { Link } from 'react-router-dom'
import type { CookieConsent as CookieConsentType } from '../types/content'

interface CookieConsentProps {
  data: CookieConsentType
  isOpen: boolean
  onAccept: () => void
  onDecline: () => void
}

/**
 * Баннер согласия на аналитические cookie.
 *
 * Сознательно без кнопки «закрыть»: бездействие не должно
 * интерпретироваться как согласие, поэтому уйти отсюда можно только
 * явным выбором. Кнопки одного размера — отказ не должен выглядеть
 * менее доступным, чем согласие.
 *
 * Высота ограничена 85vh, а текст прокручивается внутри: без этого на
 * экране 360x640 баннер занимал 96% высоты и перекрывал шапку.
 * Кнопки лежат вне области прокрутки и видны всегда.
 */
export default function CookieConsent({ data, isOpen, onAccept, onDecline }: CookieConsentProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const reduceMotion = useReducedMotion()

  // Переводим фокус на баннер, чтобы его заметили с клавиатуры
  // и через экранный диктор, а не только глазами
  useEffect(() => {
    if (isOpen) panelRef.current?.focus()
  }, [isOpen])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={panelRef}
          role="dialog"
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-text"
          tabIndex={-1}
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 24 }}
          animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 24 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-x-0 bottom-0 z-50 p-3 outline-none sm:p-6"
        >
          <div
            className="container mx-auto flex max-h-[85vh] max-w-4xl flex-col rounded-xl border border-border bg-dark-secondary p-5 shadow-glow-md sm:p-8"
            style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
          >
            {/* Прокручиваемая часть: на узких экранах текст не выталкивает кнопки */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              <h2
                id="cookie-consent-title"
                className="mb-3 text-base font-semibold text-white sm:text-lg"
              >
                {data.title}
              </h2>

              <p id="cookie-consent-text" className="mb-3 text-sm leading-relaxed text-gray-400">
                {data.text}
              </p>

              <p className="text-xs leading-relaxed text-gray-500">
                {data.note}
              </p>
            </div>

            <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-gray-500">
                {data.policyPrefix}{' '}
                <Link
                  to={data.policyUrl}
                  className="text-primary underline underline-offset-2 hover:text-primary-light"
                >
                  {data.policyLinkText}
                </Link>
              </p>

              <div className="flex flex-col gap-3 sm:flex-row sm:shrink-0">
                <button
                  type="button"
                  onClick={onDecline}
                  className="btn btn-secondary sm:min-w-[9rem]"
                >
                  {data.declineText}
                </button>
                <button
                  type="button"
                  onClick={onAccept}
                  className="btn btn-primary sm:min-w-[9rem]"
                >
                  {data.acceptText}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
